import type { SponsorDto, SponsorRequestCheckDto, SponsorRequestDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { OrgUnitSelect } from '../OrgUnitPicker';
import { Button, Dialog, Field, Input, Select, cn, useToast } from '../ui';
import { FURTHER_REQUEST_ICONS, useSponsoringOffers } from './common';

/**
 * Anfrage an einen Sponsor erfassen.
 *
 * Vor dem Speichern zeigt der Dialog, was bereits bekannt ist: Betreuung,
 * laufende und kuerzliche Anfragen. Ist der Sponsor gesperrt, laesst sich
 * nicht speichern - dieselbe Regel prueft auch der Server.
 */
export function RequestDialog({
  sponsors,
  sponsorId: fixedSponsorId,
  offerId: presetOfferId,
  onClose,
}: {
  sponsors?: SponsorDto[];
  sponsorId?: string;
  offerId?: string;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const offers = useSponsoringOffers();

  const [sponsorId, setSponsorId] = useState(fixedSponsorId ?? '');
  const [offerId, setOfferId] = useState(presetOfferId ?? '');
  const [topic, setTopic] = useState('');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [status, setStatus] = useState<'REQUESTED' | 'PLANNED'>('REQUESTED');

  const check = useQuery({
    queryKey: ['sponsor-request-check', sponsorId],
    queryFn: () => api.get<SponsorRequestCheckDto>(`/api/sponsors/${sponsorId}/request-check`),
    enabled: Boolean(sponsorId),
  });

  const selectedOffer = offers.data?.find((o) => o.id === offerId);
  const effectiveTopic = topic.trim() || selectedOffer?.title || '';

  const save = useMutation({
    mutationFn: () => api.post<SponsorRequestDto>('/api/sponsor-requests', {
      sponsorId,
      offerId: offerId || null,
      topic: effectiveTopic,
      organisationUnitId: unitId,
      status,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsor-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsoring-offers'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsor-request-check'] });
      toast.show(status === 'PLANNED' ? 'Anfrage vorgemerkt' : 'Anfrage erfasst');
      onClose();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Anfrage konnte nicht gespeichert werden', 'error'),
  });

  const blocked = check.data?.blocked ?? false;
  const sponsorName = sponsors?.find((s) => s.id === sponsorId)?.companyName;

  return (
    <Dialog
      open
      onClose={onClose}
      title={sponsorName && !fixedSponsorId ? `Anfrage an ${sponsorName}` : 'Sponsor anfragen'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!sponsorId || !effectiveTopic || blocked || check.isLoading}
          >
            {status === 'PLANNED' ? 'Vormerken' : 'Anfrage erfassen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!fixedSponsorId && (
          <Field label="Sponsor" required>
            {(id) => (
              <Select id={id} value={sponsorId} onChange={(e) => setSponsorId(e.target.value)}>
                <option value="">Bitte wählen …</option>
                {(sponsors ?? []).map((sponsor) => (
                  <option key={sponsor.id} value={sponsor.id}>
                    {FURTHER_REQUEST_ICONS[sponsor.furtherRequests]} {sponsor.companyName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {check.data && check.data.hints.length > 0 && (
          <div
            role={blocked ? 'alert' : 'status'}
            className={cn(
              'space-y-1 rounded-lg px-3 py-2 text-sm',
              blocked ? 'bg-rose-50 text-rose-800' : 'bg-amber-50 text-amber-900',
            )}
          >
            <p className="font-medium">{blocked ? 'Anfrage nicht möglich' : 'Bevor du anfragst'}</p>
            <ul className="list-disc space-y-0.5 pl-5">
              {check.data.hints.map((hint) => <li key={hint}>{hint}</li>)}
            </ul>
          </div>
        )}

        <Field label="Wofür?" hint="Werbemöglichkeit aus dem Katalog – oder unten frei beschreiben">
          {(id) => (
            <Select id={id} value={offerId} onChange={(e) => setOfferId(e.target.value)}>
              <option value="">– keine bestimmte Werbemöglichkeit –</option>
              {(offers.data ?? []).map((offer) => (
                <option key={offer.id} value={offer.id}>
                  {offer.title}{offer.free === 0 ? ' (vergeben)' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {selectedOffer && selectedOffer.free === 0 && (
          <p className="text-xs text-amber-700">
            Diese Werbemöglichkeit ist derzeit vollständig vergeben
            {selectedOffer.occupiedBy.length > 0 && ` (${selectedOffer.occupiedBy.map((o) => o.sponsorName).join(', ')})`}.
          </p>
        )}
        <Field label="Beschreibung" required={!selectedOffer} hint={selectedOffer ? `Leer lassen für „${selectedOffer.title}“` : undefined}>
          {(id) => (
            <Input id={id} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="z. B. Dressen für die U12" />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Für Bereich" hint="Optional">
            {(id) => <OrgUnitSelect id={id} value={unitId} onChange={setUnitId} allowEmpty emptyLabel="– kein bestimmter Bereich –" />}
          </Field>
          <Field label="Stand">
            {(id) => (
              <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as 'REQUESTED' | 'PLANNED')}>
                <option value="REQUESTED">Bereits angefragt</option>
                <option value="PLANNED">Nur vormerken (noch nicht angefragt)</option>
              </Select>
            )}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
